import clsx from "clsx"
import { DragEvent, useCallback, useEffect, useRef, useState } from "react"

import useFileUpload from "@hooks/useFileUpload"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import { formatFileSize, generateImagePreview } from "@utils/fileUtils"
import { validateFile } from "@utils/fileValidation"

import { Button, Icon, IconButton, ProgressBar, Tooltip } from "@atoms"
import Hint from "@atoms/Hint/Hint"

import DisplayModule from "@molecules/DisplayModule/DisplayModule"
import FileThumbnail from "@molecules/FileThumbnail/FileThumbnail"
import FormModule from "@molecules/FormModule/FormModule"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import toast from "@molecules/Toast/Toast"

import { MB } from "@constants/fileTypes"
import { DEFAULT_MAX_FILES, DEFAULT_MAX_FILE_SIZE } from "@constants/fileUpload"

import styles from "./FileUpload.module.scss"
import FileUploadProps, { FileUploadFile, PendingUploadResult } from "./FileUpload.types"

const INITIAL_VISIBLE_FILE_COUNT = 5

export default function FileUpload(props: FileUploadProps) {
    const { user } = useUser()

    const {
        className,

        maxFiles = user?.service_company?.max_file_uploads ?? DEFAULT_MAX_FILES,
        maxFileSize = user?.service_company?.max_file_upload_size != null
            ? user.service_company.max_file_upload_size * MB
            : DEFAULT_MAX_FILE_SIZE,

        acceptedFileTypes,
        label,
        dropzoneTitle = "Drag and drop files or browse...",
        dropzoneSupportText,

        onUploadingChange,
    } = props

    const { mode } = props
    const isUploadMode = mode === "attached" || mode === "pending"
    const isAttachedMode = mode === "attached"
    const isPendingMode = mode === "pending"

    const autoUpload = isUploadMode ? (props.autoUpload ?? true) : false
    const triggerUpload = isUploadMode ? (props.triggerUpload ?? false) : false
    const autoCommit = isAttachedMode ? (props.autoCommit ?? true) : true
    const triggerCommit = isAttachedMode ? (props.triggerCommit ?? false) : false
    const triggerDiscard = isUploadMode ? (props.triggerDiscard ?? false) : false
    const onCommitComplete = isAttachedMode ? props.onCommitComplete : undefined
    const onDiscardComplete = isUploadMode ? props.onDiscardComplete : undefined
    const existingFiles: Attachment[] = isAttachedMode ? (props.existingAttachments ?? []) : []
    const objectId = isAttachedMode ? props.objectId : undefined
    const sessionId = isPendingMode ? props.sessionId : undefined
    const onFileRemove = isPendingMode || mode === "controlled" ? props.onFileRemove : undefined

    const {
        uploadFiles,
        cancelUpload,
        deleteFile,
        uploadProgress,
        attachmentIds,
        commitChanges,
        discardChanges,
        deleteExistingAttachment,
        persistComponentState,
        persistedInternalFiles,
        persistedDeletedAttachmentIds,
    } = useFileUpload({
        objectType: isUploadMode ? props.objectType : undefined,
        objectId,
        isPendingMode,
        sessionId,
        autoCommit,
        onCommitComplete,
        onDiscardComplete,
        onFileUploadComplete: (file, filePath, fileUrl) => {
            setInternalFiles((prev) =>
                prev.map((f) => (f.id === file.id ? { ...f, status: "complete" as const, progress: 100 } : f)),
            )
            if (props.mode === "attached") {
                props.onFileUploadComplete?.({ file, filePath, fileUrl })
            }
        },
        onPendingFileUploadComplete: (file, filePath, fileUrl, uploadSessionId) => {
            setInternalFiles((prev) =>
                prev.map((f) => (f.id === file.id ? { ...f, status: "complete" as const, progress: 100 } : f)),
            )
            if (props.mode === "pending") {
                const result: PendingUploadResult = { file, filePath, fileUrl, sessionId: uploadSessionId }
                props.onPendingFileUploadComplete?.(result)
            }
        },
        onFileUploadError: (file, error) => {
            if (props.mode === "attached" || props.mode === "pending") {
                props.onFileUploadError?.(file, error)
            }
        },
    })

    const [internalFiles, setInternalFiles] = useState<FileUploadFile[]>(persistedInternalFiles)
    const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<Set<string>>(persistedDeletedAttachmentIds)
    const initialExistingFileCountRef = useRef<number | null>(null)
    const [isShowingAllFiles, setIsShowingAllFiles] = useState(false)

    if (initialExistingFileCountRef.current === null && existingFiles.length > 0) {
        initialExistingFileCountRef.current = existingFiles.length
    }

    const controlledFiles: FileUploadFile[] = mode === "controlled" ? (props.selectedFiles ?? []) : []
    const newFiles: FileUploadFile[] = isUploadMode ? internalFiles : controlledFiles

    // Filter out files that have been deleted in this session
    const filteredExistingFiles = existingFiles.filter(
        (existingFile) => !deletedAttachmentIds.has(String(existingFile.id)),
    )

    const wasInitiallyOverThreshold =
        initialExistingFileCountRef.current !== null &&
        initialExistingFileCountRef.current > INITIAL_VISIBLE_FILE_COUNT

    const isCollapsed = wasInitiallyOverThreshold && !isShowingAllFiles

    const visibleExistingFiles = isCollapsed
        ? filteredExistingFiles.slice(0, INITIAL_VISIBLE_FILE_COUNT)
        : filteredExistingFiles

    const hiddenFileCount = filteredExistingFiles.length - visibleExistingFiles.length
    const shouldShowMoreButton = isCollapsed && hiddenFileCount > 0

    const totalFileCount = filteredExistingFiles.length + newFiles.length

    // Sync component state to Jotai atom so it survives unmount/remount
    useEffect(() => {
        if (isUploadMode) {
            persistComponentState(internalFiles, deletedAttachmentIds)
        }
    }, [internalFiles, deletedAttachmentIds, isUploadMode, persistComponentState])

    useEffect(() => {
        if (!isUploadMode) {
            return
        }

        setInternalFiles((prev) =>
            prev.map((file) => {
                const progress = uploadProgress.get(file.id)
                if (progress) {
                    return { ...file, progress: progress.progress, status: progress.status }
                }
                return file
            }),
        )
    }, [uploadProgress, isUploadMode])

    // Track and report uploading state to parent
    const uploadingCount = internalFiles.filter((f) => f.status === "uploading").length
    const prevUploadingCountRef = useRef(uploadingCount)

    useEffect(() => {
        if (prevUploadingCountRef.current !== uploadingCount) {
            prevUploadingCountRef.current = uploadingCount
            onUploadingChange?.(uploadingCount > 0, uploadingCount)
        }
    }, [uploadingCount, onUploadingChange])

    const hasTriggeredUploadRef = useRef(false)

    useEffect(() => {
        if (!triggerUpload) {
            hasTriggeredUploadRef.current = false
            return
        }

        if (!isUploadMode || hasTriggeredUploadRef.current) {
            return
        }

        const pendingFiles = internalFiles.filter((f) => f.status === "pending")
        if (pendingFiles.length > 0) {
            hasTriggeredUploadRef.current = true
            void uploadFiles(pendingFiles)
        }
    }, [triggerUpload, isUploadMode, internalFiles, uploadFiles])

    const hasTriggeredCommitRef = useRef(false)

    useEffect(() => {
        if (!triggerCommit) {
            hasTriggeredCommitRef.current = false
            return
        }

        if (!isUploadMode || hasTriggeredCommitRef.current) {
            return
        }

        hasTriggeredCommitRef.current = true
        void commitChanges()
    }, [triggerCommit, isUploadMode, commitChanges])

    const hasTriggeredDiscardRef = useRef(false)

    useEffect(() => {
        if (!triggerDiscard) {
            hasTriggeredDiscardRef.current = false
            return
        }

        if (!isUploadMode || hasTriggeredDiscardRef.current) {
            return
        }

        hasTriggeredDiscardRef.current = true
        void discardChanges()
    }, [triggerDiscard, isUploadMode, discardChanges])

    const inputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const dragCounterRef = useRef(0)

    const hasFiles = totalFileCount > 0
    const isMaxFilesReached = totalFileCount >= maxFiles
    const remainingSlots = maxFiles - totalFileCount
    const defaultSubtitle = `Supports files up to ${formatFileSize(maxFileSize)}`

    const processFiles = useCallback(
        async (fileList: FileList | null) => {
            if (!fileList || fileList.length === 0 || isMaxFilesReached) {
                return
            }

            const allFiles = Array.from(fileList)
            const filesToProcess = allFiles.slice(0, remainingSlots)
            const droppedFileCount = allFiles.length - filesToProcess.length

            if (droppedFileCount > 0) {
                toast({
                    type: "warning",
                    variant: "detailed",
                    title: "File limit reached",
                    supportText: `${filesToProcess.length} of ${allFiles.length} files added. Maximum of ${maxFiles} files allowed.`,
                })
            }

            const validFiles: FileUploadFile[] = []
            const errors: string[] = []

            await Promise.all(
                filesToProcess.map(async (file) => {
                    const validation = validateFile(file, maxFileSize, { acceptedFileTypes })

                    if (!validation.isValid) {
                        errors.push(`${file.name}: ${validation.error}`)
                        return
                    }

                    const preview = await generateImagePreview(file)

                    validFiles.push({
                        id: crypto.randomUUID(),
                        file,
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        preview,
                        progress: 0,
                        status: "pending" as const,
                    })
                }),
            )

            if (errors.length > 0) {
                errors.forEach((error) => {
                    toast({
                        type: "error",
                        variant: "detailed",
                        title: "Invalid file",
                        supportText: error,
                    })
                })
            }

            if (validFiles.length > 0) {
                if (isUploadMode) {
                    setInternalFiles((prev) => [...prev, ...validFiles])
                    if (autoUpload) {
                        void uploadFiles(validFiles)
                    }
                } else if (props.mode === "controlled") {
                    props.onFilesSelected?.(validFiles)
                }
            }
        },
        [
            isMaxFilesReached,
            remainingSlots,
            maxFileSize,
            acceptedFileTypes,
            isUploadMode,
            autoUpload,
            uploadFiles,
            props.mode === "controlled" ? props.onFilesSelected : undefined,
        ],
    )

    const handleBrowseClick = useCallback(() => {
        if (isMaxFilesReached) {
            return
        }
        inputRef.current?.click()
    }, [isMaxFilesReached])

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            void processFiles(event.target.files)
            // Reset input value so the same file can be selected again
            if (inputRef.current) {
                inputRef.current.value = ""
            }
        },
        [processFiles],
    )

    const handleDragEnter = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()
            dragCounterRef.current += 1
            if (event.dataTransfer.items && event.dataTransfer.items.length > 0 && !isMaxFilesReached) {
                setIsDragging(true)
            }
        },
        [isMaxFilesReached],
    )

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current -= 1
        if (dragCounterRef.current === 0) {
            setIsDragging(false)
        }
    }, [])

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }, [])

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()
            setIsDragging(false)
            dragCounterRef.current = 0

            if (isMaxFilesReached) {
                return
            }

            void processFiles(event.dataTransfer.files)
        },
        [isMaxFilesReached, processFiles],
    )

    const handleRemoveFile = async (fileId: string) => {
        const fileToRemove = internalFiles.find((f) => f.id === fileId)
        const attachmentId = attachmentIds.get(fileId)

        if (isUploadMode) {
            // Try deleteFile first — it handles both committed attachments and staged additions
            const deleted = await deleteFile(fileId)
            if (!deleted) {
                // Not a tracked file in the hook — must be an in-progress upload
                cancelUpload(fileId)
            }

            if (fileToRemove) {
                setDeletedAttachmentIds((prev) => new Set(prev).add(fileToRemove.id))
            }
            setInternalFiles((prev) => prev.filter((f) => f.id !== fileId))
        }
        onFileRemove?.(attachmentId ?? fileToRemove?.name ?? fileId)
    }

    const handleRemoveExistingAttachment = async (attachment: Attachment) => {
        if (!isAttachedMode) {
            return
        }

        const deleted = await deleteExistingAttachment(String(attachment.id))
        if (deleted) {
            setDeletedAttachmentIds((prev) => new Set(prev).add(String(attachment.id)))
        }
    }

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                handleBrowseClick()
            }
        },
        [handleBrowseClick],
    )

    const renderHiddenInput = () => (
        <input
            ref={inputRef}
            type="file"
            className={styles.hiddenInput}
            multiple={maxFiles > 1}
            accept={acceptedFileTypes?.join(",")}
            disabled={isMaxFilesReached}
            onChange={handleInputChange}
        />
    )

    const renderDropzone = () => (
        <div
            className={clsx(styles.dropzone, {
                [styles.maxFilesReached]: isMaxFilesReached,
                [styles.dragging]: isDragging,
            })}
            onClick={handleBrowseClick}
            onKeyDown={handleKeyDown}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
        >
            <div className={styles.iconCopy}>
                <div className={styles.dropzoneIcon}>
                    <Icon icon={faIcon({ name: "circle-plus", style: "regular" })} size={20} />
                </div>
                <div className={styles.dropzoneContent}>
                    <span className={styles.dropzoneTitle}>{dropzoneTitle}</span>
                    <span className={styles.dropzoneSubtitle}>{dropzoneSupportText || defaultSubtitle}</span>
                </div>
            </div>
            <Button
                size="lg"
                variant="subtle"
                className={styles.browseButton}
                noDebounce={true}
                onClick={(e) => {
                    e.stopPropagation()
                    handleBrowseClick()
                }}
                isDisabled={isMaxFilesReached}
            >
                Browse
            </Button>
        </div>
    )

    const renderFileAvatar = (file: FileUploadFile) => (
        <FileThumbnail mimeType={file.type} filename={file.name} thumbnailSrc={file.preview} />
    )

    const renderExistingFileAvatar = (attachment: Attachment) => (
        <FileThumbnail
            mimeType={attachment.upload_mimetype}
            filename={attachment.upload_filename}
            thumbnailSrc={attachment.thumbnail_url}
        />
    )

    const renderFileItem = (file: FileUploadFile) => (
        <ListItem key={file.id}>
            <ListItem.AvatarAndContent
                avatar={renderFileAvatar(file)}
                content={
                    <ListItemContent
                        leftLabel={<ListItem.Label label={file.name} truncate={true} />}
                        rightLabel={<ListItem.Label label={formatFileSize(file.size)} />}
                        progressBar={file.status === "uploading" ? <ProgressBar value={file.progress} /> : undefined}
                    />
                }
            />
            <ListItem.Actions>
                <Tooltip body="Remove">
                    <IconButton
                        size="md"
                        variant="ghost"
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => void handleRemoveFile(file.id)}
                        icon={faIcon({ name: "xmark", style: "regular" })}
                    />
                </Tooltip>
            </ListItem.Actions>
        </ListItem>
    )

    const renderExistingFileItem = (attachment: Attachment) => (
        <ListItem key={attachment.id}>
            <ListItem.AvatarAndContent
                avatar={renderExistingFileAvatar(attachment)}
                content={
                    <ListItemContent
                        leftLabel={<ListItem.Label label={attachment.upload_filename} truncate={true} />}
                        rightLabel={<ListItem.Label label={formatFileSize(attachment.upload_size)} />}
                    />
                }
            />
            <ListItem.Actions>
                <Tooltip body="Remove">
                    <IconButton
                        size="md"
                        variant="ghost"
                        type="button"
                        aria-label={`Remove ${attachment.upload_filename}`}
                        onClick={() => void handleRemoveExistingAttachment(attachment)}
                        icon={faIcon({ name: "xmark", style: "regular" })}
                    />
                </Tooltip>
            </ListItem.Actions>
        </ListItem>
    )

    const renderFileList = () => (
        <>
            <Hint
                colorScheme="amber"
                title="You've reached the file limit"
                body={`You can upload up to ${maxFiles} files. Remove a file if you want to upload another one.`}
                isDisabled={!isMaxFilesReached}
                defaultPlacement="top"
            >
                <Button
                    size="lg"
                    variant="subtle"
                    isFullWidth={true}
                    noDebounce={true}
                    onClick={handleBrowseClick}
                    isDisabled={isMaxFilesReached}
                    leftIcon={faIcon({ name: "plus", style: "regular" })}
                >
                    Add attachments
                </Button>
            </Hint>
            <DisplayModule mode="headless" label="Files">
                {visibleExistingFiles.map(renderExistingFileItem)}
                {shouldShowMoreButton && (
                    <div className={styles.showMoreContainer}>
                        <Button
                            size="lg"
                            variant="ghost"
                            isFullWidth={true}
                            noDebounce={true}
                            onClick={() => setIsShowingAllFiles(true)}
                            leftIcon={faIcon({ name: "angles-up-down", style: "regular" })}
                        >
                            {`Show ${hiddenFileCount} more`}
                        </Button>
                    </div>
                )}
                {newFiles.map(renderFileItem)}
            </DisplayModule>
        </>
    )

    const renderContent = () => (
        <div className={clsx(styles.base, className)}>
            {renderHiddenInput()}
            {hasFiles ? renderFileList() : renderDropzone()}
        </div>
    )

    const labelIsString = typeof label === "string"

    return (
        <FormModule label={labelIsString ? label : { ...(label ?? { label: "Attachments" }) }}>
            {renderContent()}
        </FormModule>
    )
}
